// studio_small_09 at 135° and exposure 1.25 beat softbox on the green stage: shoulder
// highlights read as glass and the bottle edge separates from the backdrop (TAS-369).
import bottleEnvironmentUrl from '../../../../packages/bottle-viewer/assets/studio_small_09_1k.hdr?url';
import { bottleModelFor } from './bottleModels';
import { recordAssetSample } from '@tasted/utils/netTier';

let viewerModule;

export function loadRevealBottle3D() {
  // Forget a failed download so a later wine can retry once the network is back.
  viewerModule ??= import('../components/reveal/RevealBottle3D').catch((error) => {
    viewerModule = undefined;
    throw error;
  });
  return viewerModule;
}

/**
 * GET the published GLB and packaged HDR as blobs, without creating a renderer.
 * `ready` resolves with { src, hdr } object URLs for the viewer, or rejects on
 * an import/network failure. The caller owns cancel(), including after ready.
 */
export function preloadBottle(wineId) {
  const modelUrl = bottleModelFor(wineId);
  if (!modelUrl) return null;

  const controller = new AbortController();
  const objectUrls = new Set();
  const cancel = () => {
    controller.abort();
    objectUrls.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.clear();
  };
  const checkActive = () => {
    if (controller.signal.aborted) throw new DOMException('Bottle preload cancelled', 'AbortError');
  };
  const fetchResource = async (url) => {
    const startedAt = performance.now();
    const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
    if (!response.ok) throw new Error(`Bottle asset request failed (${response.status})`);
    const blob = await response.blob();
    // The heaviest thing the game fetches, timed by us. Resource timing cannot
    // see it — Supabase storage sends no Timing-Allow-Origin, so the browser
    // reports zero bytes for every model and film (TAS-398). Our own clock and
    // our own byte count are not subject to that.
    recordAssetSample({ bytes: blob.size, ms: performance.now() - startedAt });
    checkActive();
    const objectUrl = URL.createObjectURL(blob);
    objectUrls.add(objectUrl);
    return objectUrl;
  };

  const ready = Promise.all([
    loadRevealBottle3D(),
    fetchResource(modelUrl),
    fetchResource(bottleEnvironmentUrl),
  ]).then(([, src, hdr]) => {
    checkActive();
    return { src, hdr };
  }).catch((error) => {
    cancel();
    throw error;
  });
  // A page can unmount before subscribing to the result.
  void ready.catch(() => {});

  return { modelUrl, ready, cancel };
}
